import { auth, functions, db } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { doc, updateDoc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { buildFirstApprovedFieldsDotNotation, appendReportAuditEntry } from './reportAuditHelpers';

/**
 * Trigger patient report template rendering with a transactional guard.
 *
 * Used by the v1 manual review flow (RD_PR.js) where a clinician has
 * already reviewed and approved patient-specific outputs.  RD_PR.js sets
 * prReportApproved* fields in its own exit-handler transaction, so this
 * function only needs to handle the processing guard and CF invocation.
 *
 * Uses `runTransaction` to atomically check whether the patient report is
 * already processing before setting `prReportProcessingStatus = 'processing'`.
 * This prevents duplicate Cloud Function invocations when concurrent callers
 * attempt to render the same patient report simultaneously.
 *
 * Also writes `prReportApprovedAt` and `prReportProcessingStartedAt` at
 * trigger time so:
 *   - The staleness check in rerenderPatientReportIfStale correctly
 *     detects section changes that occur while the CF is running
 *   - The DocumentsDashboard self-healing logic can detect and reset
 *     stuck statuses
 *
 * The CF call is fire-and-forget.  `deadline-exceeded` errors are already
 * handled correctly here — the CF continues server-side and will update
 * status itself.
 *
 * @param {Object} params
 * @param {string} params.clinicianId - The ID of the clinician
 * @param {string} params.patientId - The ID of the patient
 * @param {string} params.reportType - The type of patient report to generate
 * @param {string} [params.cfName] - Cloud Function name to invoke. Defaults to
 *   'renderPatientReportTemplate'. Pass 'renderPatientReportTemplateTOC' for
 *   patients on the TOC ADHD Assessment Workflow.
 * @returns {Promise<Object>} - Success/error response with status
 */
export const TriggerRenderPatientReport = async ({ clinicianId, patientId, reportType, cfName }) => {
  const renderPatientReportTemplate = httpsCallable(functions, cfName || 'renderPatientReportTemplate', {
    timeout: 360000, // 6 minutes (slightly longer than server timeout of 5 minutes)
  });

  const user = auth.currentUser;
  if (!user || !user.uid) return { success: false, error: 'User not authenticated' };

  if (!clinicianId || !patientId || !reportType) {
    console.error("Missing required parameters:", { clinicianId, patientId, reportType });
    return { success: false, error: "Missing required parameters" };
  }

  try {
    const patientDocRef = doc(db, 'patients', patientId);

    // Transactional guard: only one caller wins the race to set 'processing'.
    let alreadyProcessing = false;
    let manageReports = null;

    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(patientDocRef);
      manageReports = snap.data()?.dataModel?.['6_manageReports'];

      if (manageReports?.prReportProcessingStatus === 'processing') {
        alreadyProcessing = true;
        return;
      }

      // prReportApprovedAt is set at trigger time (before the CF runs) so
      // rerenderPatientReportIfStale can detect section changes during CF
      // execution.  The renderPatientReportTemplate CF intentionally does
      // NOT write prReportApprovedAt on success — the client owns it.
      transaction.update(patientDocRef, {
        'dataModel.6_manageReports.prReportProcessingStatus': 'processing',
        'dataModel.6_manageReports.prReportProcessingStartedAt': serverTimestamp(),
        'dataModel.6_manageReports.prReportApprovedAt': serverTimestamp(),
        ...buildFirstApprovedFieldsDotNotation('pr', manageReports),
      });
    }, { maxAttempts: 10 });

    if (alreadyProcessing) {
      console.log('Skipping patient report render — already processing');
      return { success: true, data: { status: 'already-processing' } };
    }

    appendReportAuditEntry(patientId, {
      module: 'pr',
      eventType: 'approved',
      isFirstOccurrence: !manageReports?.prReportFirstApprovedAt,
    });

    // Fire-and-forget: invoke the CF without awaiting.
    renderPatientReportTemplate({
      clinicianId,
      patientId,
      reportType,
    }).then(result => {
      console.log('Patient report generated successfully:', result.data);
    }).catch(error => {
      console.error('Error in renderPatientReportTemplate function:', error);

      // deadline-exceeded means the client timed out but the CF is still
      // running server-side — don't overwrite with 'error'.
      if (error.code === 'deadline-exceeded') {
        console.log('Client-side timeout occurred, but function continues processing in background');
        return;
      }

      console.error('Actual error occurred, updating status to error');
      updateDoc(patientDocRef, {
        'dataModel.6_manageReports.prReportProcessingStatus': 'error',
      }).catch(updateError => {
        console.error('Error updating processing status:', updateError);
      });
    });

    return { success: true, data: { status: 'processing' } };

  } catch (error) {
    console.error('Error in TriggerRenderPatientReport transaction:', error);
    return { success: false, error };
  }
};
